// «Comparar tiendas» (ADR-0167) — formatting shared by the chat's table
// and its CSV export. Every figure comes from core-api's store
// comparison; this file only decides how a cell reads on screen and in
// the file, never what it is.

import type {
  StoreComparisonCell,
  StoreComparisonColumn,
  StoreComparisonResponse,
} from "@bitacorapro/api-types";
import { toRfc4180, todayIsoDate } from "./csv";

const NUM_1 = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const NUM_0 = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 });
const PCT = new Intl.NumberFormat("es-CL", { style: "percent", maximumFractionDigits: 0 });
// «22-10-2026 14:05» in Chile.
const STAMP = new Intl.DateTimeFormat("es-CL", {
  timeZone: "America/Santiago",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** Civil YYYY-MM-DD → dd-mm-aaaa, as the dashboard prints dates. */
export function civilToDisplay(civil: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : civil;
}

/** «dd-mm-aaaa hh:mm» in America/Santiago. */
export function asOfStamp(iso: string): string {
  return STAMP.format(new Date(iso)).replace(",", "");
}

export function isMaintenanceColumn(col: StoreComparisonColumn): boolean {
  return col.group === "mantenciones";
}

/** On-screen text for a non-null cell (trend arrows are added by the table). */
export function formatCell(col: StoreComparisonColumn, value: StoreComparisonCell): string {
  if (value === null) return "—";
  if (typeof value === "number") {
    if (col.kind === "percent") return PCT.format(value / 100);
    if (col.key === "score" || col.key === "tendencia") return NUM_1.format(Math.abs(value));
    return NUM_0.format(value);
  }
  if (col.kind === "date") return civilToDisplay(value);
  return value;
}

/** Sort comparator: numbers numerically, text by es collation; null last both ways. */
export function compareCells(
  a: StoreComparisonCell,
  b: StoreComparisonCell,
  dir: "asc" | "desc"
): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  const sign = dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return sign * (a - b);
  return sign * String(a).localeCompare(String(b), "es", { numeric: true });
}

/** `comparacion-tiendas_YYYY-MM-DD.{ext}` — the table's asOf day, not the download's. */
export function compareFilename(asOf: string, ext: "xlsx" | "csv"): string {
  return `comparacion-tiendas_${todayIsoDate(new Date(asOf))}.${ext}`;
}

/**
 * The es-CL CSV: «;» between fields, decimal comma, dates dd-mm-aaaa,
 * percentages as their 0–100 figure under a «(%)» header, empty fields
 * for no data. Rows in the order given (the table's current sort).
 */
export function buildCompareCsv(
  table: Pick<StoreComparisonResponse, "columns">,
  rows: StoreComparisonResponse["rows"]
): string {
  const header = table.columns.map((c) => (c.kind === "percent" ? `${c.label} (%)` : c.label));
  const data = rows.map((r) =>
    table.columns.map((c) => {
      const v = r.cells[c.key] ?? null;
      if (v === null) return null;
      if (typeof v === "number") return v;
      return c.kind === "date" ? civilToDisplay(v) : v;
    })
  );
  return toRfc4180([header, ...data], { delimiter: ";", decimalComma: true });
}
